/**
 * Admin API — Users, Roles & Permissions domain module.
 *
 * Extracted from the AdminService god class in services/auth.ts and converted
 * to the central web API client. Method names, signatures, and observable
 * behavior (error messages, status branching, dedup cache keys, response
 * normalization) are preserved exactly; a later step composes these back into
 * the `adminService` export.
 */

import { HttpError } from '@turn-connect/shared';
import { webApiClient, unwrap } from '../apiClient';
import { dedup, extractItems } from './helpers';
import type { User, RoleTemplate } from '../auth';
import type {
  ApiUser,
  ApiLocation,
  ApiPermission,
  ApiPermissionCatalogItem,
  ApiRole,
  ApiRoleCreateAccess,
  ApiWorkSessionPolicy,
  ApiCheckDuplicateUserResponse,
  ApiUserDepartmentAccess,
  ApiUpdateOverridesResponse,
  ApiResetOverridesResponse,
  ApiHasOverridesResponse,
} from '../../types/api';

/**
 * Reproduce the legacy `j.error || j.message || body` error-message pattern.
 * The client surfaces the parsed JSON error body as `errorBody` and the raw
 * text (or stringified JSON) as `error`.
 */
function errorMessage(res: { error?: string; errorBody?: any }, fallback: string): string {
  const eb: any = res.errorBody;
  return eb?.error || eb?.message || res.error || fallback;
}

/**
 * Reproduce the legacy pattern that only extracted a message when the error
 * body parsed as JSON (non-JSON bodies fell through to the fallback).
 */
function jsonErrorMessage(res: { error?: string; errorBody?: any }, fallback: string): string {
  const eb: any = res.errorBody;
  if (eb) return eb.error || eb.message || res.error || fallback;
  return fallback;
}

// ============================================================================
// Users
// ============================================================================

// Users returned by the list endpoints map to entities.User in the spec;
// `id` is re-asserted required (always populated; callers key lists on it).
type ListedUser = ApiUser & { id: string };

/** Largest page GET /accounts/{id}/users returns (backend MaxUserPageSize). A
 * roster of exactly this many users is truncated — callers switch to `search`. */
export const ACCOUNT_USERS_MAX_LIMIT = 500;

// Get users for an account, which may include location information
// (spec: GET /accounts/{account_id}/users -> services.ListUsersResponse)
// `search` narrows by first/last/full name or email server-side, before paging,
// so pickers on accounts larger than ACCOUNT_USERS_MAX_LIMIT can reach everyone.
//
// `lightweight` drops the per-user role / department-access / override
// enrichment the backend otherwise does for every user in the page — an N+1
// that made a 500-user roster take seconds to land. Pickers want it: they need
// id, name and `location_ids`, which the lightweight shape still carries.
export async function getAccountUsers(
  accountId: string,
  locationId?: string,
  opts: { include_deleted?: boolean; limit?: number; search?: string; lightweight?: boolean } = {}
): Promise<ListedUser[]> {
  const qp = new URLSearchParams();
  if (locationId) qp.set('location_id', locationId);
  if (opts.include_deleted) qp.set('include_deleted', 'true');
  if (opts.lightweight) qp.set('lightweight', 'true');
  const search = opts.search?.trim();
  if (search) qp.set('search', search);
  // Callers want the WHOLE account roster (recipient pickers, billing
  // aggregation), not a paginated page. Without an explicit limit the backend
  // defaults to 20 and silently truncates larger accounts — e.g. the schedule
  // report "Also send to" picker showed only the first page of users. Request
  // the backend's max page (CapLimit caps at 500) so every active user appears;
  // callers may still override.
  qp.set('limit', String(opts.limit ?? ACCOUNT_USERS_MAX_LIMIT));
  const qs = qp.toString();
  return dedup(`getAccountUsers:${accountId}:${qs}`, async () => {
    const res = await webApiClient.get<any>(`/accounts/${accountId}/users${qs ? `?${qs}` : ''}`);
    if (!res.success) {
      throw new Error(`Failed to fetch account users: ${res.status} - ${res.error}`);
    }
    return extractItems<ListedUser>(res.data, ['users']);
  });
}

// Get users for a specific location (preferred when location filter is selected)
// (spec: GET /locations/{location_id}/users -> services.ListUsersResponse of
// entities.User. Return stays any[] for now: MentionTextarea unions this with
// searchUsers' shared User — whose nullable deleted_at conflicts with its
// local User type — and a typed return surfaces that pre-existing conflict.)
export async function getLocationUsers(locationId: string, search?: string, limit?: number): Promise<any[]> {
  const params = new URLSearchParams();
  if (search) params.set('search', search);
  if (limit) params.set('limit', String(limit));
  const qs = params.toString();
  const cacheKey = search ? '' : `getLocationUsers:${locationId}`; // Don't cache search results
  const fetcher = async () => {
    const res = await webApiClient.get<any>(`/locations/${locationId}/users${qs ? `?${qs}` : ''}`);
    if (!res.success) {
      throw new Error(`Failed to fetch location users: ${res.status} - ${res.error}`);
    }
    return extractItems(res.data, ['users']);
  };
  return cacheKey ? dedup(cacheKey, fetcher) : fetcher();
}

// User-location associations
// (spec: GET /users/{user_id}/locations -> services.SuccessResponse + { data: entities.Location[] })
export async function getUserLocations(userId: string): Promise<ApiLocation[]> {
  const json = await unwrap(webApiClient.get<any>(`/users/${userId}/locations`), 'Failed to fetch user locations');
  return extractItems<ApiLocation>(json, ['locations']);
}

// Location role mutations are handled via role assign/remove

// Department access APIs
// (spec: GET /users/{user_id}/departments -> services.ListDepartmentsResponse
// of plain entities.Department items. Access-grant metadata (access level,
// reason/notes, expires_at) is NOT part of this response — the spec points to
// GET /departments/{id}/users (entities.UserDepartmentAccess) for per-user
// grant details — so the mapping only carries id/name/location_id.)
export async function getUserDepartments(userId: string): Promise<Array<{ id: string; name: string; location_id?: string }>> {
  const json = await unwrap(webApiClient.get<any>(`/users/${encodeURIComponent(userId)}/departments`), 'Failed to fetch user departments');
  const list = extractItems(json, ['departments']);
  return (list as any[]).map((d: any) => ({
    id: String(d.id ?? d.department_id ?? ''),
    name: String(d.name ?? d.department_name ?? d.title ?? d.id ?? ''),
    location_id: d.location_id ?? d.location?.id,
  }));
}

export async function grantDepartmentAccess(
  departmentId: string,
  userId: string,
  payload?: {
    accessType?: 'read' | 'write' | 'admin';
    expiresAt?: string;
    reason?: string;
    blind_dispatch_enabled?: boolean;
    max_concurrent_work_orders?: number;
  }
): Promise<void> {
  const did = encodeURIComponent(departmentId);
  const uid = encodeURIComponent(userId);
  const body: any = {};
  if (payload?.accessType) body.access_type = payload.accessType;
  if (payload?.expiresAt) body.expires_at = payload.expiresAt;
  if (payload?.reason) body.reason = payload.reason;
  if (payload?.blind_dispatch_enabled !== undefined) body.blind_dispatch_enabled = payload.blind_dispatch_enabled;
  if (payload?.max_concurrent_work_orders !== undefined) body.max_concurrent_work_orders = payload.max_concurrent_work_orders;
  await unwrap(webApiClient.post<any>(`/departments/${did}/users/${uid}`, body), 'Failed to grant department access');
}

export async function revokeDepartmentAccess(departmentId: string, userId: string): Promise<void> {
  const did = encodeURIComponent(departmentId);
  const uid = encodeURIComponent(userId);
  await unwrap(webApiClient.delete<any>(`/departments/${did}/users/${uid}`), 'Failed to revoke department access');
}

export async function assignUserRole(userId: string, roleId: string, locationId?: string): Promise<void> {
  const pid = encodeURIComponent(roleId);
  const uid = encodeURIComponent(userId);
  const body: { location_id?: string } = {};
  if (locationId) {
    body.location_id = locationId;
  }
  await unwrap(webApiClient.post<any>(`/roles/${pid}/users/${uid}`, body), 'Failed to assign role');
}

/**
 * Remove a role assignment from a user.
 *
 * **Always pass `locationId` for location-type roles.** The backend's
 * unscoped delete (when locationId is omitted) wipes every user_roles
 * row matching (userId, roleId), regardless of location — so a
 * single "remove Sarah's role at L1" call would also revoke her at
 * L2, L3, L4 if all four use the same role definition (the default
 * Location Manager / Commission Tech / etc roles do exactly that).
 *
 * locationId scopes the delete to the one assignment row at that
 * location. Omit only when you genuinely mean "remove this account-
 * scoped role from this user" — i.e. the role has no location_id.
 */
export async function removeUserRole(userId: string, roleId: string, locationId?: string): Promise<void> {
  const pid = encodeURIComponent(roleId);
  const uid = encodeURIComponent(userId);
  const qs = locationId ? `?location_id=${encodeURIComponent(locationId)}` : '';
  await unwrap(webApiClient.delete<any>(`/roles/${pid}/users/${uid}${qs}`), 'Failed to remove role');
}

// No active-users endpoint exists on the backend (verified against every
// backend branch). This used to probe /departments/{id}/active_users,
// /locations/{id}/departments/{id}/active_users and /sessions/active — none
// of which exist — and finally GET /users with location_id/department_id/
// active_session params the backend ignores, whose { items } envelope the
// extraction never read. Every path therefore resolved to [], which is now
// returned directly (no network call). Wire up a real endpoint here if the
// backend ever documents active-session filtering.
export async function getActiveUsers(_locationId: string, _departmentId: string): Promise<any[]> {
  return [];
}

// Get all users in a department (for assignment purposes)
// (spec: GET /departments/{department_id}/users ->
// services.ListDepartmentUsersResponse, the standard list envelope with
// entities.UserDepartmentAccess items: user_id / first_name / last_name /
// email plus grant details. The Record intersection keeps callers free to
// splice in synthetic entries — AssignUserToServiceModal prepends a
// self-assign row. The old probing fallbacks (/users?department_id=…,
// /departments/{id}/members) called routes that don't exist on any backend
// branch and were removed; on a non-403 failure this degrades to [] like the
// exhausted probe loop did.)
export async function getDepartmentUsers(
  departmentId: string,
  opts: { search?: string; service_id?: string; work_order_id?: string; limit?: number } = {}
): Promise<Array<ApiUserDepartmentAccess & Record<string, any>>> {
  // With an empty `search`, the backend returns suggestions ranked by recency
  // of assignment to `service_id` (90-day window), falling back to
  // department-wide recency then name — already interleaved server-side.
  // With `search`, it applies the standard user search within the assignable
  // set, so typing finds users far beyond any first page.
  // Mobile/shared twin: packages/shared/src/services/adminService.ts
  // (AdminService.getDepartmentUsers) — keep query-string behavior in sync.
  const qp = new URLSearchParams();
  if (opts.search?.trim()) qp.set('search', opts.search.trim());
  if (opts.service_id) qp.set('service_id', opts.service_id);
  if (opts.work_order_id) qp.set('work_order_id', opts.work_order_id);
  if (opts.limit) qp.set('limit', String(opts.limit));
  const qs = qp.toString();
  const res = await webApiClient.get<any>(`/departments/${departmentId}/users${qs ? `?${qs}` : ''}`);
  if (res.status === 403) {
    throw new Error('INSUFFICIENT_PERMISSIONS: You do not have permission to view users.');
  }
  if (!res.success) return [];
  const json = res.data;
  return Array.isArray(json) ? json : (json?.items ?? []);
}

export async function getUsers(opts: { include_deleted?: boolean } = {}): Promise<User[]> {
  const qp = new URLSearchParams();
  if (opts.include_deleted) qp.set('include_deleted', 'true');
  const res = await webApiClient.get<any>(`/users${qp.toString() ? `?${qp.toString()}` : ''}`);

  if (!res.success) {
    throw new Error('Failed to fetch users');
  }

  // OpenAPI returns a common envelope with `items`; normalize to array
  const list = extractItems<User>(res.data, ['users']);
  return list as User[];
}

// New: server-side user search per OpenAPI (e.g., GET /users?search={email}&limit=1)
export async function searchUsers(
  query: string,
  opts: { limit?: number; offset?: number; account_id?: string; location_id?: string; include_deleted?: boolean } = {}
): Promise<User[]> {
  const qp = new URLSearchParams();
  qp.set('search', String(query || ''));
  if (opts.limit != null) qp.set('limit', String(opts.limit));
  if (opts.offset != null) qp.set('offset', String(opts.offset));
  if (opts.account_id) qp.set('account_id', String(opts.account_id));
  if (opts.location_id) qp.set('location_id', String(opts.location_id));
  if (opts.include_deleted) qp.set('include_deleted', 'true');
  const json = await unwrap(webApiClient.get<any>(`/users?${qp.toString()}`), 'Failed to search users');
  return extractItems<User>(json, ['users']);
}

// Paginated user search — same /users endpoint as searchUsers but
// returns the full envelope so callers can drive server-side
// pagination without a parallel count query. `query` may be empty to
// fetch a page without filtering. Account / location scopes are
// honored server-side via the matching query params.
export async function searchUsersPaginated(
  query: string,
  opts: { limit?: number; offset?: number; account_id?: string; location_id?: string; include_deleted?: boolean } = {}
): Promise<{ items: User[]; total: number; count: number; limit: number; offset: number; has_more: boolean }> {
  const qp = new URLSearchParams();
  qp.set('search', String(query || ''));
  if (opts.limit != null) qp.set('limit', String(opts.limit));
  if (opts.offset != null) qp.set('offset', String(opts.offset));
  if (opts.account_id) qp.set('account_id', String(opts.account_id));
  if (opts.location_id) qp.set('location_id', String(opts.location_id));
  if (opts.include_deleted) qp.set('include_deleted', 'true');
  const json = await unwrap(webApiClient.get<any>(`/users?${qp.toString()}`), 'Failed to list users');
  const items = extractItems<User>(json, ['users']);
  const limit = Number(json?.limit ?? opts.limit ?? items.length);
  const offset = Number(json?.offset ?? opts.offset ?? 0);
  const total = Number(json?.total ?? items.length);
  const count = Number(json?.count ?? items.length);
  // Trust the server's has_more when present; fall back to a page-end
  // computation that handles the "exact-fit final page" edge case.
  const hasMore = json?.has_more !== undefined ? !!json.has_more : offset + items.length < total;
  return { items, total, count, limit, offset, has_more: hasMore };
}

export async function createUser(user: Partial<User>): Promise<User> {
  const res = await webApiClient.post<User>('/users', user);

  if (!res.success) {
    throw new Error((res.errorBody as any)?.error || 'Failed to create user');
  }

  return res.data as User;
}

/**
 * Check if a user with the given email or phone already exists
 * Returns information about the duplicate user including disabled status
 */
// (spec: GET /users/check-duplicate -> services.CheckDuplicateUserResponse;
// the intersection re-asserts the boolean flags the backend always sets,
// since generated properties are all optional.)
export async function checkDuplicateUser(params: { email?: string; phone?: string; accountId?: string }): Promise<
  ApiCheckDuplicateUserResponse & {
    email_exists: boolean;
    phone_exists: boolean;
    is_disabled: boolean;
    same_account: boolean;
  }
> {
  const searchParams = new URLSearchParams();
  if (params.email) searchParams.set('email', params.email);
  if (params.phone) searchParams.set('phone', params.phone);
  if (params.accountId) searchParams.set('account_id', params.accountId);

  const res = await webApiClient.get<any>(`/users/check-duplicate?${searchParams.toString()}`);

  if (!res.success) {
    throw new Error((res.errorBody as any)?.error || 'Failed to check for duplicate user');
  }

  return res.data;
}

export async function deleteUser(userId: string): Promise<void> {
  const res = await webApiClient.delete<any>(`/users/${userId}`);
  if (!res.success) {
    throw new Error(errorMessage(res, 'Failed to delete user'));
  }
}

export async function undeleteUser(userId: string): Promise<void> {
  const res = await webApiClient.post<any>(`/users/${userId}/undelete`);
  if (!res.success) {
    throw new Error(errorMessage(res, 'Failed to undelete user'));
  }
}

export async function hardDeleteUser(userId: string): Promise<void> {
  const res = await webApiClient.delete<any>(`/admin/users/${userId}`);
  if (!res.success) {
    throw new Error(errorMessage(res, 'Failed to permanently delete user'));
  }
}

// Pass currentPassword when a regular user (non-admin) is changing
// their OWN password — the backend requires it for the self-update
// path and returns 401 INVALID_CURRENT_PASSWORD if it doesn't match.
// Admin-on-other-user and sysadmin-on-self skip it.
export async function updateUserPassword(userId: string, password: string, currentPassword?: string): Promise<void> {
  const reqBody: Record<string, string> = { password };
  if (currentPassword) reqBody.current_password = currentPassword;
  const res = await webApiClient.put<any>(`/users/${userId}/password`, reqBody);
  if (!res.success) {
    throw new Error(errorMessage(res, 'Failed to update password'));
  }
}

// ============================================================================
// User permissions
// ============================================================================

// (spec: GET /users/{user_id}/permissions -> entities.UserWithRoles: the flat
// { user_id, email, first/last_name, roles: RoleWithPermissions[],
// permissions: string[] } shape. Verified against the backend handler
// (turn-connect#303): there are NO per-account/per-location grouping buckets
// in the response and NO expansion query parameters — include_locations /
// include_accounts were always ignored. The former bucket-normalization arms
// and query params were dead code and have been removed; the always-empty
// accounts/locations mirror keys are kept as literals so the returned shape
// is unchanged for real payloads (pinned by the characterization tests in
// __tests__/usersRoles.getUserPermissions.test.ts).)
export async function getUserPermissions(userId: string): Promise<any> {
  const json = await unwrap(webApiClient.get<any>(`/users/${userId}/permissions`), 'Failed to fetch user permissions');
  const payload = json?.data ?? json?.item ?? json;

  const ensureArray = (...candidates: any[]): any[] => {
    for (const candidate of candidates) {
      if (!candidate) continue;
      if (Array.isArray(candidate)) return candidate;
      if (Array.isArray(candidate?.items)) return candidate.items;
      if (Array.isArray(candidate?.data)) return candidate.data;
      if (Array.isArray(candidate?.results)) return candidate.results;
    }
    return [];
  };

  const normalizePermission = (perm: any) => {
    if (!perm) return null;
    if (typeof perm === 'string') {
      const key = perm.trim();
      if (!key) return null;
      return { id: key, key, label: key };
    }
    const key = String(perm.key ?? perm.id ?? perm.name ?? '').trim();
    const label = perm.label ?? perm.name ?? key;
    if (!key && !label) return null;
    const resolvedId = perm.id ?? (key || label);
    return { ...perm, id: resolvedId, key: key || label, label: label || key };
  };

  const dedupeRoles = (items: any[]) => {
    const seen = new Set<string>();
    const result: any[] = [];
    for (const item of items) {
      const key = `${item?.id ?? ''}|${item?.location_id ?? ''}|${item?.scope ?? ''}`;
      if (seen.has(key)) continue;
      seen.add(key);
      result.push(item);
    }
    return result;
  };

  const normalizeRoleEntry = (entry: any) => {
    const raw = entry?.role || entry?.profile || entry;
    const id = raw?.id ?? raw?.role_id ?? raw?.profile_id ?? entry?.role_id ?? entry?.profile_id ?? entry?.id;
    const name = raw?.name ?? raw?.role_name ?? raw?.profile_name ?? entry?.name ?? entry?.label ?? (id ? String(id) : 'Role');
    const locationId = entry?.location_id ?? raw?.location_id ?? entry?.location?.id ?? raw?.location?.id ?? null;
    const accountId = entry?.account_id ?? raw?.account_id ?? entry?.account?.id ?? raw?.account?.id ?? null;
    const scope = raw?.scope ?? entry?.scope ?? (locationId ? 'location' : accountId ? 'account' : raw?.scope);
    const permissionBuckets = [
      ensureArray(raw?.permissions),
      ensureArray(entry?.permissions),
      ensureArray(raw?.permission_assignments),
      ensureArray(entry?.permission_assignments),
      ensureArray(raw?.permission_keys),
      ensureArray(entry?.permission_keys),
    ];
    const permissionMap = new Map<string, any>();
    const pushPermission = (perm: any) => {
      const normalized = normalizePermission(perm);
      if (!normalized?.key) return;
      if (!permissionMap.has(normalized.key)) permissionMap.set(normalized.key, normalized);
    };
    for (const bucket of permissionBuckets) {
      for (const item of bucket) pushPermission(item);
    }
    const permissionList = Array.from(permissionMap.values());
    const permissionKeys = permissionList.map((p) => p.key).filter(Boolean);
    return {
      ...entry,
      role: {
        ...(raw || {}),
        id: id ? String(id) : undefined,
        name,
        scope,
        location_id: locationId ? String(locationId) : undefined,
        account_id: accountId ? String(accountId) : undefined,
        permissions: permissionList,
        permission_keys: permissionKeys,
      },
      id: id ? String(id) : entry?.id,
      name,
      scope,
      location_id: locationId ? String(locationId) : undefined,
      account_id: accountId ? String(accountId) : undefined,
      permissions: permissionList,
      permission_keys: permissionKeys,
    };
  };

  const roles = dedupeRoles(
    ensureArray(payload.roles, payload.role_assignments, payload.profiles, payload.items).map((r: any) => normalizeRoleEntry(r))
  );

  const permissionMap = new Map<string, any>();
  const collectPermissions = (list: any[]) => {
    for (const item of list) {
      const normalized = normalizePermission(item);
      if (!normalized?.key) continue;
      if (!permissionMap.has(normalized.key)) permissionMap.set(normalized.key, normalized);
    }
  };

  collectPermissions(ensureArray(payload.permissions, payload.permission_keys, payload.permission_ids));
  for (const role of roles) collectPermissions(role.permissions || []);

  const permissionList = Array.from(permissionMap.values());
  const permissionKeys = permissionList.map((p) => p.key).filter(Boolean);

  return {
    ...payload,
    roles,
    role_assignments: roles,
    // The backend never sends per-account/per-location buckets; these keys
    // are kept (always empty) so the returned shape is unchanged.
    accounts: [],
    account_assignments: [],
    account_roles: [],
    locations: [],
    location_assignments: [],
    location_roles: [],
    permissions: permissionList,
    permission_list: permissionList,
    permission_keys: permissionKeys,
    permission_strings: permissionKeys,
  };
}

/**
 * Get effective permissions for a user with source information
 * Shows which permissions come from roles vs custom grants/denies
 */
export async function getUserEffectivePermissions(
  userId: string,
  locationId?: string
): Promise<{
  user_id: string;
  role_id?: string;
  role_name?: string;
  permissions: Array<{
    key: string;
    label: string;
    category: string;
    source: 'role' | 'grant' | 'deny';
    source_role_id?: string;
    source_role_name?: string;
    is_granted: boolean;
    override_info?: {
      granted_by_name?: string;
      granted_at: string;
      reason?: string;
      expires_at?: string;
    };
  }>;
}> {
  const params = new URLSearchParams();
  if (locationId) params.set('location_id', locationId);
  return unwrap(
    webApiClient.get<any>(`/users/${userId}/permissions/effective${params.toString() ? `?${params.toString()}` : ''}`),
    'Failed to fetch effective permissions'
  );
}

/**
 * Get custom permission overrides for a user
 */
export async function getUserPermissionOverrides(
  userId: string,
  locationId?: string
): Promise<
  Array<{
    id: string;
    user_id: string;
    permission_key: string;
    action: 'grant' | 'deny';
    account_id?: string;
    location_id?: string;
    granted_by?: string;
    granted_by_name?: string;
    reason?: string;
    expires_at?: string;
    created_at: string;
    updated_at: string;
  }>
> {
  const params = new URLSearchParams();
  if (locationId) params.set('location_id', locationId);
  return unwrap(
    webApiClient.get<any>(`/users/${userId}/permissions/overrides${params.toString() ? `?${params.toString()}` : ''}`),
    'Failed to fetch permission overrides'
  );
}

/**
 * Update user permission overrides (batch)
 *
 * (spec: PUT /users/{id}/permissions/overrides ->
 * handlers.UpdateOverridesResponse; updated/message re-asserted required —
 * the backend always sets them and callers rely on it. DELETE and
 * has-overrides below follow the same pattern with
 * handlers.ResetOverridesResponse / handlers.HasOverridesResponse.)
 */
export async function updateUserPermissionOverrides(
  userId: string,
  overrides: Array<{
    permission_key: string;
    action: 'grant' | 'deny';
    location_id?: string;
    reason?: string;
    expires_at?: string;
  }>
): Promise<ApiUpdateOverridesResponse & { updated: number; message: string }> {
  return unwrap(webApiClient.put<any>(`/users/${userId}/permissions/overrides`, { overrides }), 'Failed to update permission overrides');
}

/**
 * Reset user permissions to role defaults (remove all overrides)
 */
export async function resetUserPermissions(
  userId: string,
  locationId?: string
): Promise<ApiResetOverridesResponse & { removed: number; message: string }> {
  const params = new URLSearchParams();
  if (locationId) params.set('location_id', locationId);
  return unwrap(
    webApiClient.delete<any>(`/users/${userId}/permissions/overrides${params.toString() ? `?${params.toString()}` : ''}`),
    'Failed to reset permissions'
  );
}

/**
 * Check if a user has custom permission overrides
 */
export async function checkUserHasOverrides(
  userId: string,
  locationId?: string
): Promise<ApiHasOverridesResponse & { has_overrides: boolean; count: number }> {
  const params = new URLSearchParams();
  if (locationId) params.set('location_id', locationId);
  return unwrap(
    webApiClient.get<any>(`/users/${userId}/permissions/has-overrides${params.toString() ? `?${params.toString()}` : ''}`),
    'Failed to check overrides'
  );
}

// ============================================================================
// Permissions & roles
// ============================================================================

// (spec: GET /permissions -> services.ListPermissionsResponse of
// entities.Permission; id/key/label re-asserted required — the backend always
// populates them and downstream key->id translation relies on it.)
export async function listPermissions(limit = 100, offset = 0): Promise<Array<ApiPermission & { id: string; key: string; label: string }>> {
  return dedup(`listPermissions:${limit}:${offset}`, async () => {
    const json = await unwrap(webApiClient.get<any>(`/permissions?limit=${limit}&offset=${offset}`), 'Failed to fetch permissions');
    return Array.isArray(json) ? json : (json?.items ?? json?.data ?? json?.permissions ?? []);
  });
}

/** Why the signed-in user cannot give a permission (GET /permissions?account_id=). */
export type PermissionNotGrantableReason = NonNullable<ApiPermissionCatalogItem['not_grantable_reason']>;

/**
 * A catalog permission as seen by the signed-in user for one account.
 * `grantable` / `not_grantable_reason` come only with `account_id`; an older
 * API leaves them out, and an absent `grantable` means "can give" (the
 * behaviour before the flag existed). id/key/label re-asserted required.
 */
export type GrantablePermission = ApiPermissionCatalogItem & { id: string; key: string; label: string };

/** Which kinds of role the signed-in user may create in the account. */
export type RoleCreateRights = Required<ApiRoleCreateAccess>;

/**
 * The permission catalog for building roles in one account, with what the
 * signed-in user may give. `canCreateRoles` is undefined against an API that
 * does not send it (callers fall back to their own permission checks).
 */
export async function listPermissionsForAccount(
  accountId: string,
  limit = 1000,
  offset = 0
): Promise<{ permissions: GrantablePermission[]; canCreateRoles?: RoleCreateRights }> {
  const qs = `account_id=${encodeURIComponent(accountId)}&limit=${limit}&offset=${offset}`;
  return dedup(`listPermissionsForAccount:${qs}`, async () => {
    const json = await unwrap(webApiClient.get<any>(`/permissions?${qs}`), 'Failed to fetch permissions');
    const permissions = Array.isArray(json) ? json : (json?.items ?? json?.data ?? json?.permissions ?? []);
    const rights = !Array.isArray(json) ? json?.can_create_roles : undefined;
    const canCreateRoles =
      rights && typeof rights === 'object' ? { account: rights.account === true, location: rights.location === true } : undefined;
    return { permissions, canCreateRoles };
  });
}

/**
 * List roles with optional filtering by role_type
 * @param params.role_type - 'account' for admin roles, 'location' for daily operation roles
 * @param params.account_id - Filter by account (required for account-scoped roles)
 * @param params.scope - Legacy parameter for backwards compatibility (use role_type instead)
 */
export async function listRoles(
  params: {
    scope?: 'global' | 'account'; // Legacy: no longer supports 'location' scope
    role_type?: 'account' | 'location'; // New: filter by role type
    account_id?: string;
    /** With account_id: mark location roles `assignable` for this store (account roles stay account-wide). */
    location_id?: string;
    include_deleted?: boolean; // Include soft-deleted roles
    limit?: number;
    offset?: number;
  } = {}
  // spec: GET /roles -> services.ListRolesResponse of services.RoleListItem
  // (role + permissions, and with account_id: assignable / not_assignable_reason).
  // `role` stays open (Record intersection) because callers read fields the
  // spec omits from entities.Role (e.g. user_count).
): Promise<
  Array<{
    role: ApiRole & Record<string, any>;
    permissions: Array<ApiPermission & Record<string, any>>;
    assignable?: boolean;
    not_assignable_reason?: 'not_held' | 'staff_only' | 'no_assign_right' | null;
  }>
> {
  const q = new URLSearchParams();
  if (params.scope) {
    q.set('scope', params.scope);
  }
  if (params.role_type) {
    q.set('role_type', params.role_type);
  }
  if (params.account_id) q.set('account_id', params.account_id);
  if (params.account_id && params.location_id) q.set('location_id', params.location_id);
  if (params.include_deleted) q.set('include_deleted', 'true');
  if (params.limit != null) q.set('limit', String(params.limit));
  if (params.offset != null) q.set('offset', String(params.offset));
  const qs = q.toString();
  return dedup(`listRoles:${qs}`, async () => {
    const json = await unwrap(webApiClient.get<any>(`/roles${qs ? `?${qs}` : ''}`), 'Failed to fetch roles');
    const list = Array.isArray(json) ? json : (json?.items ?? json?.data ?? json?.roles ?? []);
    return list as any[];
  });
}

// NOTE: the former permission-grid methods (GET/PUT
// /accounts/{account_id}/roles/permission-grid) and role-type assignment
// methods (POST/DELETE /users/{user_id}/roles/account and
// /users/{user_id}/roles/location/{location_id}) were deleted: those routes
// never existed on any backend branch, so every call failed. Role
// assignment goes through assignUserRole / removeUserRole above; per-role
// permission edits go through updateRole.

export async function createRole(payload: {
  name: string;
  scope: 'global' | 'account';
  role_type?: 'account' | 'location';
  account_id?: string;
  permissions?: string[];
  permission_ids?: string[];
  /** When true, users holding only grant-requiring roles for a location must
   * have explicit department grants there (zero grants = no department access). */
  requires_department_grants?: boolean;
}): Promise<ApiRole> {
  // spec: POST /roles -> 201 entities.Role
  // API expects CreateCustomRoleRequest with permission_ids and scope
  if (!payload?.name || !payload?.scope) {
    throw new Error('Role name and scope are required');
  }
  if (payload.scope === 'account' && !payload.account_id) {
    throw new Error('account_id is required for account-scoped roles');
  }

  let permissionIds: string[] | undefined = payload.permission_ids;
  if (!permissionIds) {
    const keys = payload.permissions || [];
    if (!Array.isArray(keys) || keys.length === 0) {
      throw new Error('At least one permission is required');
    }
    // Translate permission keys to IDs via /permissions
    const perms = await listPermissions(1000, 0).catch(() => []);
    const byKey = new Map((perms as any[]).map((p) => [p.key, p.id]));
    permissionIds = keys.map((k) => {
      const id = byKey.get(k);
      if (!id) throw new Error(`Unknown permission key: ${k}`);
      return id as string;
    });
  }

  const body: any = {
    name: payload.name,
    scope: payload.scope,
    role_type: payload.role_type || 'location',
    permission_ids: permissionIds,
  };
  if (payload.account_id) body.account_id = payload.account_id;
  if (payload.requires_department_grants !== undefined) {
    body.requires_department_grants = payload.requires_department_grants;
  }

  const res = await webApiClient.post<any>('/roles', body);
  if (!res.success) {
    // HttpError keeps the status and body (code PERMISSION_NOT_GRANTABLE,
    // details.keys) so the role screens can say which permissions were refused.
    throw new HttpError(res.status ?? 0, errorMessage(res, 'Failed to create role'), res.errorBody);
  }
  return res.data;
}

export async function updateRole(
  roleId: string,
  payload: {
    name: string;
    permissions?: string[];
    permission_ids?: string[];
    updated_by_user_id: string;
    /** Optional; omitted = leave unchanged on the backend. */
    requires_department_grants?: boolean;
  }
): Promise<ApiRole> {
  // spec: PUT /roles/{role_id} -> entities.Role
  // PUT /roles/{id} expects full payload: { name, permission_ids, updated_by_user_id }
  if (!payload?.name) throw new Error('Role name is required');
  if (!payload?.updated_by_user_id) throw new Error('updated_by_user_id is required');

  let permissionIds: string[] | undefined = payload.permission_ids;
  if (!permissionIds && payload.permissions) {
    // Translate permission keys to IDs using /permissions
    const perms = await listPermissions(1000, 0).catch(() => []);
    const byKey = new Map((perms as any[]).map((p: any) => [p.key, p.id]));
    permissionIds = payload.permissions.map((k) => {
      const id = byKey.get(k);
      if (!id) throw new Error(`Unknown permission key: ${k}`);
      return id as string;
    });
  }

  const body: any = {
    name: payload.name,
    permission_ids: permissionIds ?? [],
    updated_by_user_id: payload.updated_by_user_id,
  };
  if (payload.requires_department_grants !== undefined) {
    body.requires_department_grants = payload.requires_department_grants;
  }

  const res = await webApiClient.put<any>(`/roles/${roleId}`, body);
  if (!res.success) {
    throw new HttpError(res.status ?? 0, errorMessage(res, 'Failed to update role'), res.errorBody);
  }
  return res.data;
}

export async function deleteRole(roleId: string): Promise<void> {
  await unwrap(webApiClient.delete<any>(`/roles/${roleId}`), 'Failed to delete role');
}

export async function undeleteRole(roleId: string): Promise<void> {
  await unwrap(webApiClient.post<any>(`/roles/${roleId}/undelete`), 'Failed to enable role');
}

// =============================================================================
// Role Template API Methods (DEPRECATED)
// Role templates have been removed in favor of centralized account-level roles.
// These methods are kept for backwards compatibility but will return empty results.
// Use listRoles with role_type='location' instead.
// =============================================================================

/**
 * @deprecated Role templates have been removed. Use listRoles with role_type='location' instead.
 */
export async function listAccountTemplates(
  accountId: string,
  options: { limit?: number; offset?: number } = {}
): Promise<{
  items: RoleTemplate[];
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
  count: number;
}> {
  const params = new URLSearchParams();
  if (options.limit) params.set('limit', String(options.limit));
  if (options.offset) params.set('offset', String(options.offset));

  return unwrap(
    webApiClient.get<any>(`/accounts/${accountId}/role-templates${params.toString() ? `?${params.toString()}` : ''}`),
    'Failed to fetch role templates'
  );
}

/**
 * @deprecated Role templates have been removed. Use listRoles with role_type='location' instead.
 */
export async function getAccountTemplate(accountId: string, templateId: string): Promise<RoleTemplate> {
  return unwrap(webApiClient.get<any>(`/accounts/${accountId}/role-templates/${templateId}`), 'Failed to fetch role template');
}

/**
 * @deprecated Role templates have been removed. Use createRole with role_type='location' instead.
 */
export async function createAccountTemplate(
  accountId: string,
  payload: {
    name: string;
    description?: string;
    auto_provision_to_locations?: boolean;
    permission_ids: string[];
  }
): Promise<RoleTemplate> {
  const res = await webApiClient.post<any>(`/accounts/${accountId}/role-templates`, payload);
  if (!res.success) {
    throw new Error(errorMessage(res, 'Failed to create role template'));
  }
  return res.data;
}

/**
 * @deprecated Role templates have been removed. Use updateRole instead.
 */
export async function updateAccountTemplate(
  accountId: string,
  templateId: string,
  payload: {
    name?: string;
    description?: string;
    auto_provision_to_locations?: boolean;
    permission_ids?: string[];
  }
): Promise<RoleTemplate> {
  const res = await webApiClient.put<any>(`/accounts/${accountId}/role-templates/${templateId}`, payload);
  if (!res.success) {
    throw new Error(errorMessage(res, 'Failed to update role template'));
  }
  return res.data;
}

/**
 * @deprecated Role templates have been removed.
 */
export async function deleteAccountTemplate(accountId: string, templateId: string): Promise<void> {
  const res = await webApiClient.delete<any>(`/accounts/${accountId}/role-templates/${templateId}`);
  if (!res.success) {
    throw new Error(errorMessage(res, 'Failed to delete role template'));
  }
}

/**
 * @deprecated Role templates have been removed.
 */
export async function getTemplateDerivedRoles(
  accountId: string,
  templateId: string,
  options: { limit?: number; offset?: number } = {}
): Promise<{
  items: any[];
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
  count: number;
}> {
  const params = new URLSearchParams();
  if (options.limit) params.set('limit', String(options.limit));
  if (options.offset) params.set('offset', String(options.offset));

  return unwrap(
    webApiClient.get<any>(
      `/accounts/${accountId}/role-templates/${templateId}/derived-roles${params.toString() ? `?${params.toString()}` : ''}`
    ),
    'Failed to fetch derived roles'
  );
}

/**
 * Create a location role from a template
 * @deprecated Role templates have been removed. Use createRole() directly with role_type='location'.
 */
export async function createRoleFromTemplate(accountId: string, templateId: string, locationId: string): Promise<any> {
  const res = await webApiClient.post<any>(`/accounts/${accountId}/role-templates/${templateId}/instantiate`, {
    location_id: locationId,
  });
  if (!res.success) {
    throw new Error(errorMessage(res, 'Failed to create role from template'));
  }
  return res.data;
}

// ============================================================================
// Profiles (legacy compat stub)
// ============================================================================

export async function getAllProfiles(): Promise<any[]> {
  console.warn('getAllProfiles: Stub method - returning empty array');
  return [];
}

// ============================================================================
// User Payroll Management (user-scoped, not location-scoped)
// ============================================================================

export async function getUserPayroll(userId: string): Promise<{
  user_id: string;
  flat_rate_compensation?: string;
  percent_of_ticket_compensation?: string;
  hourly_clock_rate?: string;
  external_account_id?: string;
}> {
  const res = await webApiClient.get<any>(`/users/${userId}/payroll`);

  if (!res.success) {
    throw new Error(errorMessage(res, 'Failed to get user payroll'));
  }

  return res.data;
}

/**
 * Body for PUT /users/{id}/payroll.
 *
 * Every key is REQUIRED on purpose. The API leaves an omitted field untouched
 * and clears an explicit null, so an optional key is the difference between
 * "keep it" and "clear it" — and the create-user flow silently dropped
 * external_account_id by leaving it out. Callers state all four; use
 * buildPayrollPayload rather than assembling this by hand.
 */
export interface UserPayrollUpdate {
  flat_rate_compensation: string | null;
  percent_of_ticket_compensation: string | null;
  hourly_clock_rate: string | null;
  external_account_id: string | null;
}

export async function updateUserPayroll(userId: string, payroll: UserPayrollUpdate): Promise<void> {
  const res = await webApiClient.put<any>(`/users/${userId}/payroll`, payroll);

  if (!res.success) {
    throw new Error(errorMessage(res, 'Failed to update user payroll'));
  }
}

// ============================================================================
// Session Policy Methods
// ============================================================================

// Session policy responses map to entities.WorkSessionPolicy in the spec.
// The intersection re-asserts the identifiers and non-nullable flags the
// backend always serializes (non-pointer Go struct fields) — callers
// (TimesheetPolicyForm's WorkSessionPolicy) rely on them being present.
type SessionPolicy = ApiWorkSessionPolicy & {
  id: string;
  account_id: string;
  enable_auto_checkout: boolean;
  enable_geofencing: boolean;
  geofence_radius_meters: number;
  enable_fraud_detection: boolean;
  require_consistent_device: boolean;
  flag_unusual_hours: boolean;
  created_at: string;
  updated_at: string;
};

export async function listSessionPolicies(accountId: string): Promise<SessionPolicy[]> {
  // spec: GET /accounts/{account_id}/session-policies -> entities.WorkSessionPolicy[]
  return dedup(`listSessionPolicies:${accountId}`, async () => {
    const res = await webApiClient.get<any>(`/accounts/${accountId}/session-policies`);
    if (!res.success) {
      throw new Error(errorMessage(res, 'Failed to fetch session policies'));
    }
    return extractItems<SessionPolicy>(res.data);
  });
}

export async function createSessionPolicy(accountId: string, policy: any): Promise<SessionPolicy> {
  // spec: POST /accounts/{account_id}/session-policies -> 201 entities.WorkSessionPolicy
  // (request body is services.CreateWorkSessionPolicyRequest; param stays loose
  // because callers build payloads with UI-only seed fields)
  const res = await webApiClient.post<SessionPolicy>(`/accounts/${accountId}/session-policies`, policy);
  if (!res.success) {
    throw new Error(jsonErrorMessage(res, 'Failed to create session policy'));
  }
  return res.data as SessionPolicy;
}

export async function getSessionPolicy(policyId: string): Promise<SessionPolicy | null> {
  // spec: GET /session-policies/{policy_id} -> entities.WorkSessionPolicy
  const res = await webApiClient.get<SessionPolicy>(`/session-policies/${policyId}`);
  if (!res.success) {
    if (res.status === 404) return null;
    throw new Error('Failed to fetch session policy');
  }
  return res.data as SessionPolicy;
}

export async function getSessionPolicyForLocation(accountId: string, locationId: string): Promise<SessionPolicy | null> {
  // spec: GET /accounts/{account_id}/locations/{location_id}/session-policy -> entities.WorkSessionPolicy
  return dedup(`getSessionPolicyForLocation:${accountId}:${locationId}`, async () => {
    const res = await webApiClient.get<SessionPolicy>(`/accounts/${accountId}/locations/${locationId}/session-policy`);
    if (!res.success) {
      if (res.status === 404) return null;
      throw new Error('Failed to fetch session policy for location');
    }
    return res.data as SessionPolicy;
  });
}

export async function updateSessionPolicy(policyId: string, policy: any): Promise<SessionPolicy> {
  // spec: PUT /session-policies/{policy_id} -> entities.WorkSessionPolicy
  const res = await webApiClient.put<SessionPolicy>(`/session-policies/${policyId}`, policy);
  if (!res.success) {
    throw new Error(jsonErrorMessage(res, 'Failed to update session policy'));
  }
  return res.data as SessionPolicy;
}

export async function deleteSessionPolicy(policyId: string): Promise<void> {
  const res = await webApiClient.delete<any>(`/session-policies/${policyId}`);
  if (!res.success) {
    throw new Error('Failed to delete session policy');
  }
}

// ── Work-order required-fields policy ────────────────────────────────────────

/**
 * The account required-fields policy: the flat list plus `locked`
 * (["required_fields"] when the account has locked the list for every
 * location) and the account row's `locked_fields`.
 */
export interface WorkOrderFieldPolicyResponse {
  required_fields: string[];
  locked?: string[];
  account?: { required_fields?: string[]; locked_fields?: string[] } | null;
}

export async function getWorkOrderFieldPolicy(accountId: string): Promise<WorkOrderFieldPolicyResponse> {
  // spec: GET /accounts/{account_id}/work-order-field-policy -> entities.WorkOrderFieldPolicyEffective
  const res = await webApiClient.get<any>(`/accounts/${accountId}/work-order-field-policy`);
  if (!res.success) {
    throw new Error('Failed to fetch work order field policy');
  }
  return res.data as WorkOrderFieldPolicyResponse;
}

/** `lockedFields` (["required_fields"] | []) is sent only when given — absent keeps the current lock. */
export async function updateWorkOrderFieldPolicy(
  accountId: string,
  requiredFields: string[],
  lockedFields?: string[]
): Promise<WorkOrderFieldPolicyResponse> {
  // spec: PUT /accounts/{account_id}/work-order-field-policy -> entities.WorkOrderFieldPolicyEffective
  const body: { required_fields: string[]; locked_fields?: string[] } = { required_fields: requiredFields };
  if (lockedFields !== undefined) body.locked_fields = lockedFields;
  const res = await webApiClient.put<any>(`/accounts/${accountId}/work-order-field-policy`, body);
  if (!res.success) {
    throw new Error(jsonErrorMessage(res, 'Failed to update work order field policy'));
  }
  return res.data as WorkOrderFieldPolicyResponse;
}

// ── Work-order edit-lock policy (how long closed WOs stay editable) ─────────

export async function getWorkOrderEditLockPolicy(accountId: string): Promise<import('@turn-connect/shared').WorkOrderEditLockPolicy> {
  // spec: GET /accounts/{account_id}/work-order-edit-lock-policy -> entities.WorkOrderEditLockPolicy
  const res = await webApiClient.get<any>(`/accounts/${accountId}/work-order-edit-lock-policy`);
  if (!res.success) {
    throw new Error('Failed to fetch work order edit-lock policy');
  }
  return res.data as import('@turn-connect/shared').WorkOrderEditLockPolicy;
}

/** The PUT body may carry `locked_fields` to replace the account's lock list ([] unlocks all); absent keeps it. */
export async function updateWorkOrderEditLockPolicy(
  accountId: string,
  policy: Pick<import('@turn-connect/shared').WorkOrderEditLockPolicy, 'mode' | 'days' | 'grace_days' | 'week_start' | 'lock_time'> & {
    locked_fields?: string[];
  }
): Promise<import('@turn-connect/shared').WorkOrderEditLockPolicy> {
  // spec: PUT /accounts/{account_id}/work-order-edit-lock-policy -> entities.WorkOrderEditLockPolicy
  const res = await webApiClient.put<any>(`/accounts/${accountId}/work-order-edit-lock-policy`, policy);
  if (!res.success) {
    throw new Error(jsonErrorMessage(res, 'Failed to update work order edit-lock policy'));
  }
  return res.data as import('@turn-connect/shared').WorkOrderEditLockPolicy;
}

// ── External share-link expiry policy (how long share links stay valid) ─────
// Type + presentation helpers live in @turn-connect/shared (shareLinkPolicy.ts).

export async function getShareLinkPolicy(accountId: string): Promise<import('@turn-connect/shared').ShareLinkPolicy> {
  // spec: GET /accounts/{account_id}/share-link-policy -> entities.ShareLinkPolicy
  const res = await webApiClient.get<any>(`/accounts/${accountId}/share-link-policy`);
  if (!res.success) {
    throw new Error('Failed to fetch share-link policy');
  }
  return res.data as import('@turn-connect/shared').ShareLinkPolicy;
}

export async function updateShareLinkPolicy(
  accountId: string,
  policy: import('@turn-connect/shared').ShareLinkPolicy
): Promise<import('@turn-connect/shared').ShareLinkPolicy> {
  // spec: PUT /accounts/{account_id}/share-link-policy -> entities.ShareLinkPolicy
  const res = await webApiClient.put<any>(`/accounts/${accountId}/share-link-policy`, policy);
  if (!res.success) {
    throw new Error(jsonErrorMessage(res, 'Failed to update share-link policy'));
  }
  return res.data as import('@turn-connect/shared').ShareLinkPolicy;
}

export async function getWorkOrderFieldPolicyForLocation(accountId: string, locationId: string): Promise<{ required_fields: string[] }> {
  // spec: GET /accounts/{account_id}/locations/{location_id}/work-order-field-policy
  return dedup(`getWorkOrderFieldPolicyForLocation:${accountId}:${locationId}`, async () => {
    const res = await webApiClient.get<any>(`/accounts/${accountId}/locations/${locationId}/work-order-field-policy`);
    if (!res.success) {
      throw new Error('Failed to fetch work order field policy for location');
    }
    return res.data as { required_fields: string[] };
  });
}

// ============================================================================
// Composed export
// ============================================================================

export const usersRolesAdmin = {
  // Users
  getAccountUsers,
  getLocationUsers,
  getUserLocations,
  getUserDepartments,
  grantDepartmentAccess,
  revokeDepartmentAccess,
  assignUserRole,
  removeUserRole,
  getActiveUsers,
  getDepartmentUsers,
  getUsers,
  searchUsers,
  searchUsersPaginated,
  createUser,
  checkDuplicateUser,
  deleteUser,
  undeleteUser,
  hardDeleteUser,
  updateUserPassword,
  // User permissions
  getUserPermissions,
  getUserEffectivePermissions,
  getUserPermissionOverrides,
  updateUserPermissionOverrides,
  resetUserPermissions,
  checkUserHasOverrides,
  // Permissions & roles
  listPermissions,
  listPermissionsForAccount,
  listRoles,
  createRole,
  updateRole,
  deleteRole,
  undeleteRole,
  // Role templates (deprecated)
  listAccountTemplates,
  getAccountTemplate,
  createAccountTemplate,
  updateAccountTemplate,
  deleteAccountTemplate,
  getTemplateDerivedRoles,
  createRoleFromTemplate,
  // Profiles (stub)
  getAllProfiles,
  // Payroll
  getUserPayroll,
  updateUserPayroll,
  // Session policies
  listSessionPolicies,
  createSessionPolicy,
  getSessionPolicy,
  getSessionPolicyForLocation,
  updateSessionPolicy,
  deleteSessionPolicy,
  getWorkOrderFieldPolicy,
  updateWorkOrderFieldPolicy,
  getWorkOrderEditLockPolicy,
  updateWorkOrderEditLockPolicy,
  getShareLinkPolicy,
  updateShareLinkPolicy,
  getWorkOrderFieldPolicyForLocation,
};
